import {
  Card,
  Container,
  Grid,
  Group,
  LoadingOverlay,
  Menu,
  Radio,
  rem,
  Stack,
  Text,
  Title,
  UnstyledButton
} from "@mantine/core";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {
  adjustToTimeZone,
  ContainerLevelHistorySearchCriteria,
  containerLevelsSelector,
  containerSelector,
  isLevelType,
  LevelType,
  useGetContainerQuery,
  useSearchContainerLevelsQuery
} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {useNavigate, useSearchParams} from "react-router-dom";
import {ContainerLevelChart} from "../../components/chart/ContainerLevelChart/ContainerLevelChart";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {useAuth} from "../../providers/AuthProvider/AuthProvider";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {FromToDateToolbar} from "../../components/toolbar/FromToDateToolbar/FromToDateToolbar";
import {Result} from "../../components/result/Result";

export const ContainerLevelSearchPageRoute = () => {
  const [params, _setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const id = params.get("containerId") ?? undefined;

  if (!id) {
    navigate(-1);
    return (
      <ThemedPage>
        {(classes) => (
          <Container className={classes.container} fluid={true} py="md">
            <Result title={"Error"} message={"Container Not Found"}/>
          </Container>
        )}
      </ThemedPage>
    );
  }

  return <ContainerLevelSearchPage containerId={id}/>;
};

type Props = {
  containerId?: string;
}

export const ContainerLevelSearchPage = (props: Props) => {
  const {containerId} = props

  const {t} = useTranslation(["translation", "common"]);
  const {regionId} = useApp()
  const auth = useAuth()

  const [dateCriteria, setDateCriteria] = useState<{ fromDate: Date, toDate: Date | undefined }>()
  const [levelType, setLevelType] = useState<LevelType>("volume")

  const selectContainer = useMemo(() => {
    return containerSelector()
  }, []);

  const {container} = useGetContainerQuery(containerId ?? skipToken, {
    selectFromResult: (result) => ({
      container: selectContainer(result),
      isContainerLoading: result.isFetching || result.isLoading,
    })
  });

  const selectContainerLevels = useMemo(() => {
    return containerLevelsSelector()
  }, []);

  const searchCriteria: ContainerLevelHistorySearchCriteria | any = useMemo(() => {
    if (dateCriteria?.fromDate && regionId && containerId) {
      return {
        fromDttm: adjustToTimeZone(dateCriteria.fromDate).getTime(),
        toDttm: dateCriteria.toDate ? adjustToTimeZone(dateCriteria.toDate).getTime() : undefined,
        regionId: regionId,
        containerId: containerId,
        authentication: auth.userId,
      }
    } else {
      return skipToken
    }
  }, [auth.userId, containerId, dateCriteria?.fromDate, dateCriteria?.toDate, regionId])

  const {containerLevels, isLoading, refetch} = useSearchContainerLevelsQuery(searchCriteria, {
    selectFromResult: (result) => ({
      ...result,
      containerLevels: selectContainerLevels(result),
      isLoading: result.isLoading || result.isFetching,
    })
  });

  const onFilterChange = useCallback((fromDate: Date, toDate: Date | undefined) => {
    setDateCriteria({
      fromDate: fromDate,
      toDate: toDate
    })
  }, []);

  const onLevelTypeChange = useCallback((value: string) => {
    if (isLevelType(value)) {
      setLevelType(value)
    } else {
      setLevelType("volume")
    }
  }, [])

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                <Group position="apart">
                  <Title order={4}>
                    {t("page.containerlevelsearch.title", "SCADA Search", {ns: "translation"})}
                  </Title>
                  <Menu
                    width={260}
                    position="bottom-end"
                    transitionProps={{transition: "pop-top-right"}}
                    withinPortal
                  >
                    <Menu.Target>
                      <UnstyledButton>
                        <Group spacing={7}>
                          <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                            Actions
                          </Text>
                          <IconChevronDown size={rem(12)} stroke={1.5}/>
                        </Group>
                      </UnstyledButton>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item onClick={() => refetch()}>
                        {t("commons.action.refresh", "Refresh", {ns: "common"})}
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>
                {container &&
                  <Title order={6}>
                    {container.name}
                  </Title>
                }
                <FromToDateToolbar
                  defaultValues={{
                    fromDate: dateCriteria?.fromDate,
                    toDate: dateCriteria?.toDate
                  }}
                  loading={isLoading}
                  onSubmit={onFilterChange}/>
              </Card>

              <LoadingOverlay visible={isLoading}/>

              <Container p="md" fluid={true}>
                <Grid>
                  <Grid.Col span={8}>
                    <Container fluid={true}>
                      <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                        <ContainerLevelChart
                          id={`${containerId}_container_level_chart`}
                          levels={containerLevels}
                          type={levelType}/>
                      </Card>
                    </Container>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Card shadow="sm" padding="lg" radius="md" withBorder>
                      <Radio.Group
                        name="levelType"
                        value={levelType}
                        onChange={onLevelTypeChange}>
                        <Stack mt="xs">
                          <Radio value="volume"
                                 label={t("common.entity.containerlevel.reading.type.volume", "Volume")}/>
                          <Radio value="height" label={t("common.entity.containerlevel.reading.type.height", "Level")}/>
                        </Stack>
                      </Radio.Group>
                    </Card>
                  </Grid.Col>
                </Grid>
              </Container>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
